import React from 'react';
import type { CompetitorItem } from '../../../../services/optimizationService';

interface CompetitorGapsModalProps {
  competitor: CompetitorItem | null;
  onClose: () => void;
}

export const CompetitorGapsModal: React.FC<CompetitorGapsModalProps> = ({ competitor, onClose }) => {
  if (!competitor) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn">
      <div 
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-950/30 dark:to-amber-950/30">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-xl bg-orange-100 dark:bg-orange-900/40 text-orange-600 dark:text-orange-400 flex items-center justify-center font-bold text-lg">
              <i className="ri-sword-line"></i>
            </div>
            <div>
              <div className="flex items-center space-x-2">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                  Keyword Gap Analysis
                </h3>
                <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-orange-100 text-orange-700 dark:bg-orange-900/50 dark:text-orange-300">
                  {competitor.keyword_gaps.length} Gaps
                </span>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
                Target Competitor: <span className="font-bold text-gray-900 dark:text-gray-200">{competitor.domain}</span>
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {/* Info banner */}
        <div className="p-4 bg-orange-50/60 dark:bg-orange-900/20 border-b border-orange-100 dark:border-orange-900/30 text-xs text-orange-800 dark:text-orange-200 flex items-center space-x-2">
          <i className="ri-information-line text-base text-orange-600 flex-shrink-0"></i>
          <span>
            These keywords represent search terms where {competitor.domain} is actively outranking your domain or capturing search share where you currently don't rank.
          </span>
        </div>

        {/* Keyword Table */}
        <div className="p-6 overflow-y-auto flex-1">
          {competitor.keyword_gaps.length === 0 ? (
            <p className="text-center py-8 text-sm text-gray-500 dark:text-gray-400">
              No direct keyword gaps recorded for this competitor.
            </p>
          ) : (
            <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
              <table className="w-full text-left text-sm">
                <thead className="bg-gray-50 dark:bg-gray-700/50 text-xs uppercase font-semibold text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                  <tr>
                    <th className="px-4 py-3">Keyword</th>
                    <th className="px-4 py-3 text-center">{competitor.domain}</th>
                    <th className="px-4 py-3 text-center">Your Position</th>
                    <th className="px-4 py-3 text-right">Advantage</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {competitor.keyword_gaps.map((gap, idx) => {
                    const diff = gap.user_position ? gap.user_position - gap.competitor_position : null;
                    return (
                      <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                        <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">
                          "{gap.keyword}"
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300">
                            Pos {gap.competitor_position}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          {gap.user_position != null ? (
                            <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                              Pos {gap.user_position}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400 italic">Unranked (&gt;100)</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {diff != null && diff > 0 ? (
                            <span className="text-xs font-bold text-rose-600 dark:text-rose-400">
                              +{diff} pos lead
                            </span>
                          ) : (
                            <span className="text-xs font-semibold text-amber-600 dark:text-amber-400">
                              Untapped
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end bg-gray-50 dark:bg-gray-900/40">
          <button
            onClick={onClose}
            className="px-5 py-2 rounded-xl bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 font-medium text-sm transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
